import type { LandingPageV2Props } from 'cms';
import landingPageV2EntryLookup from 'cms/entries/landingPageV2';
import RichTextRenderer from 'cms/renderer';
import type { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';
import { ReturnExperienceContextProvider } from 'helpers/ReturnExperienceContext';
import { StickyCTABarContextProvider } from 'helpers/StickyCTABarContext';
import LandingPageV2Layout, {
  getLandingpageV2ServerSideProps,
  isMissingJobInLoaderData,
} from 'pages/shared/LandingPageV2Layout';

// GROWTH-1355 POC. Path-based JD URL: /careers/open-positions/jobs/<slug>-<uuid>.
// Routes through the same JD Contentful entry as the legacy /careers/open-positions/jobs
// query-string URL by overriding the slug Next.js extracts from the URL. The actual
// UUID extraction happens inside OpenPositions.tsx's loader against pageProps.url.
export const getServerSideProps: CacheSafeGetServerSideProps<LandingPageV2Props> = async (
  context,
) => {
  const overrideContext = {
    ...context,
    params: { ...context.params, slug: ['careers', 'open-positions', 'jobs'] },
    query: { ...context.query, slug: ['careers', 'open-positions', 'jobs'] },
  };
  const result = await getLandingpageV2ServerSideProps(overrideContext);

  // Short-circuit unknown / closed jobs by redirecting to the listings page BEFORE
  // caching. Otherwise a stale or invalid UUID renders as a 200 "role isn't available"
  // page that gets edge-cached at a unique URL, polluting search results with
  // soft-404s. 307 keeps the redirect non-permanent — a closed req can reopen, and
  // we don't want crawlers caching JD-URL → /careers/open-positions.
  if ('props' in result) {
    const props = result.props as LandingPageV2Props;
    if (isMissingJobInLoaderData(props.idToLoaderData)) {
      return {
        redirect: { destination: '/careers/open-positions', permanent: false },
      };
    }
  }

  // Edge-cache the JD response so per-render Rippling fetches don't hammer their
  // undocumented v2 endpoint on viral inbound traffic. 5 min fresh + 24h stale-
  // while-revalidate keeps copies serving instantly while we revalidate behind.
  if (context.res && 'props' in result) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=86400');
  }
  return result;
};

const JobDetailPage = (props: LandingPageV2Props) => (
  <ReturnExperienceContextProvider showReturnExperience={props.fields.showReturnExperienceBanner}>
    <StickyCTABarContextProvider>
      <LandingPageV2Layout {...props}>
        <RichTextRenderer.Component
          body={props.fields.body}
          idToLoaderData={props.idToLoaderData}
          entryLookup={landingPageV2EntryLookup}
        />
      </LandingPageV2Layout>
    </StickyCTABarContextProvider>
  </ReturnExperienceContextProvider>
);

export default JobDetailPage;
